import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    Output
} from '@angular/core';
import { UntypedFormGroup, Validators } from '@angular/forms';
import { EditPanelComponent } from '@Work/Accounts/proprietary/controls/form-controls/edit-panel/edit-panel.component';
import { DocumentObjectService } from '@Core/models/proprietary/document/document-object.service';
import { AppService } from '@Core/services/proprietary/app.service';
import { SearchService } from '@Core/services/proprietary/search.service';

@Component({
    standalone: false,
    selector: 'ep-document-file-edit-panel',
    templateUrl: './document-file-edit-panel.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocumentFileEditPanelComponent extends EditPanelComponent implements OnInit, OnDestroy {
    ignorePageState = true; // overrides automatic readonly, error display mode from page state
    readOnlyMode = true; //override readonly mode

    @Input()
    set document(value: any) {
        this.setFormData(value);
    }

    @Output()
    filingReferanceChange = new EventEmitter<boolean>();

    constructor(
        private documentOS: DocumentObjectService,
        private injector: Injector,
        private app: AppService,
        private searchSrv: SearchService
    ) {
        super(injector);

        this.form = new UntypedFormGroup(
            {
                Document: this.fb.group({
                    Doc_ID: [''],
                    Edit_Status: [''],
                    Doc_Name: ['', Validators.required],
                    Doc_Desc: [''],
                    Doc_Class: [''],
                    Created_By: this.fb.group({
                        Entity_ID: [''],
                        Entity_Name: [''],
                        Date: ['']
                    }),
                    Last_Revised_By: this.fb.group({
                        Entity_ID: [''],
                        Entity_Name: [''],
                        Date: ['']
                    }),
                    Filed_Ind: [''],
                    Protected_Ind: [''],
                    Sent_Ind: ['']
                }),
                NewFiling: this.fb.group({
                    Contract: this.fb.group({
                        Contract_ID: [''],
                        Contract_Num: ['']
                    }),
                    Claim: this.fb.group({
                        Claim_ID: [''],
                        Book_Claim_Num: ['']
                    }),
                    Entity: this.fb.group({
                        Entity_ID: [''],
                        Entity_Name: [''],
                        Role_Type: ['']
                    })
                })
            },
            { updateOn: 'blur' }
        );
    }

    ngOnInit() {
        super.ngOnInit(); // TODO!! this is problematic -- if we forget this things don't work -- same problem for ngDestroy!!!   
        //  Perhap extends is NOT a good solution.   A service?'
        //NOTE:  Normally we subscribe to form.valueChanges.  We subscribe toi just Document formgroup  since NewFiling is virtual
        this.formChangeSub = this.form.controls.Document.valueChanges.subscribe((value) => {
            this.documentOS.updateDocument(this.form.getRawValue().Document);
        });
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }

    applyRules() {
        //this.disableControl('NewFiling.Claim');
    }

    setFormData(value: any) {
        if (value) {
            this.form.controls.Document.patchValue(value, { emitEvent: false });
            this.applyRules();
        }
    }

    claimSearchResult(data: any) {
        console.log(JSON.stringify(data));
        if (data) {
            data.forEach((claim) => {
                // update entity & contract on form
                this.form.controls.NewFiling.patchValue(
                    {
                        Entity: {
                            Role_Type: 'CN',
                            Entity_ID: claim.Consumer.Entity_ID,
                            Entity_Name: claim.Consumer.Entity_Name
                        },
                        Contract: {
                            Contract_ID: claim.Contract.Contract_ID,
                            Contract_Num: claim.Contract.Contract_Num
                        }
                    },
                    { emitEvent: false }
                );

                // update contract/claim references on existing new filings
                this.documentOS.updateFilingReferences({
                    Contract: {
                        Contract_ID: claim.Contract.Contract_ID,
                        Contract_Num: claim.Contract.Contract_Num
                    },
                    Claim: {
                        Claim_ID: claim.Claim_ID,
                        Book_Claim_Num: claim.Book_Claim_Num
                    }
                });

                // add filings to document state
                this.documentOS.addFiling({
                    Selected_Ind: 'Y',
                    Distribute: { Method: 'F' },
                    Entity: {
                        Role_Type: 'CN',
                        Entity_ID: claim.Consumer.Entity_ID,
                        Entity_Name: claim.Consumer.Entity_Name
                    },
                    Book: {
                        Entity_ID: this.app.dsSystemInfo.objectNode.Context.Book.Entity_ID
                    },
                    Book_Loc: {
                        Entity_ID: this.app.dsSystemInfo.objectNode.Context.Book_Loc.Entity_ID
                    },
                    Contract: {
                        Contract_ID: claim.Contract.Contract_ID,
                        Contract_Num: claim.Contract.Contract_Num
                    },
                    Claim: {
                        Claim_ID: claim.Claim_ID,
                        Book_Claim_Num: claim.Book_Claim_Num
                    }
                });
            });
            this.filingReferanceChange.emit(true);
        }
    }

    claimSearchCleared() {
        //clear claim search inputs

        // remove claim references on existing new filings
        this.documentOS.updateFilingReferences({
            Claim: {
                Claim_ID: null,
                Book_Claim_Num: null
            }
        });
    }

    contractSearchCleared() {
        //clear claim search inputs
        this.form.controls.NewFiling.patchValue(
            {
                Claim: {
                    Claim_ID: null,
                    Book_Claim_Num: null
                }
            },
            { emitEvent: false }
        );

        // remove contract/claim references on existing new filings
        this.documentOS.updateFilingReferences({
            Contract: {
                Contract_ID: null,
                Contract_Num: null
            },
            Claim: {
                Claim_ID: null,
                Book_Claim_Num: null
            }
        });
    }

    contractSearchResult(data: any[]) {
        //console.log(JSON.stringify(data));
        if (data) {
            data.forEach((contract) => {
                if (contract.Consumer && contract.Consumer.Entity_ID > 0) {
                    // update entity on form
                    this.form.controls.NewFiling.patchValue(
                        {
                            Entity: {
                                Role_Type: 'CN',
                                Entity_ID: contract.Consumer.Entity_ID,
                                Entity_Name: contract.Consumer.Entity_Name
                            }
                        },
                        { emitEvent: false }
                    );
                    // clear claim on form
                    this.form.controls.NewFiling.patchValue(
                        {
                            Claim: {
                                Claim_ID: null,
                                Book_Claim_Num: null
                            }
                        },
                        { emitEvent: false }
                    );

                    // update contract/claim references on existing new filings
                    this.documentOS.updateFilingReferences({
                        Contract: {
                            Contract_ID: contract.Contract_ID,
                            Contract_Num: contract.Contract_Num
                        },
                        Claim: {
                            Claim_ID: null,
                            Book_Claim_Num: null
                        }
                    });

                    // add filings to document state
                    this.documentOS.addFiling({
                        Selected_Ind: 'Y',
                        Distribute: { Method: 'F' },
                        Entity: {
                            Role_Type: 'CN',
                            Entity_ID: contract.Consumer.Entity_ID,
                            Entity_Name: contract.Consumer.Entity_Name
                        },
                        Book: {
                            Entity_ID: this.app.dsSystemInfo.objectNode.Context.Book.Entity_ID
                        },
                        Book_Loc: {
                            Entity_ID: this.app.dsSystemInfo.objectNode.Context.Book_Loc.Entity_ID
                        },
                        Contract: {
                            Contract_ID: contract.Contract_ID,
                            Contract_Num: contract.Contract_Num
                        }
                    });
                }
                if (contract.Broker && contract.Broker.Entity_ID > 0) {
                    this.documentOS.addFiling({
                        Selected_Ind: 'N',
                        Distribute: { Method: 'F' },
                        Entity: {
                            Role_Type: 'BK',
                            Entity_ID: contract.Broker.Entity_ID,
                            Entity_Name: contract.Broker.Entity_Name
                        },
                        Book: {
                            Entity_ID: this.app.dsSystemInfo.objectNode.Context.Book.Entity_ID
                        },
                        Book_Loc: {
                            Entity_ID: this.app.dsSystemInfo.objectNode.Context.Book_Loc.Entity_ID
                        },
                        Contract: {
                            Contract_ID: contract.Contract_ID,
                            Contract_Num: contract.Contract_Num
                        }
                    });
                }
                if (contract.Supplier && contract.Supplier.Entity_ID > 0) {
                    this.documentOS.addFiling({
                        Selected_Ind: 'N',
                        Distribute: { Method: 'F' },
                        Entity: {
                            Role_Type: 'SP',
                            Entity_ID: contract.Supplier.Entity_ID,
                            Entity_Name: contract.Supplier.Entity_Name
                        },
                        Book: {
                            Entity_ID: this.app.dsSystemInfo.objectNode.Context.Book.Entity_ID
                        },
                        Book_Loc: {
                            Entity_ID: this.app.dsSystemInfo.objectNode.Context.Book_Loc.Entity_ID
                        },
                        Contract: {
                            Contract_ID: contract.Contract_ID,
                            Contract_Num: contract.Contract_Num
                        }
                    });
                }
            });
            this.filingReferanceChange.emit(true);
        }
    }

    entitySearchResult(data: any) {
        //console.log(JSON.stringify(data));
        if (data) {
            data.forEach((entity) => {
                if (entity.Entity_ID > 0) {
                    this.documentOS.addFiling({
                        Selected_Ind: 'Y',
                        Distribute: { Method: 'F' },

                        Entity: {
                            Role_Type: this.getSelectedEntityRole(entity),
                            Entity_ID: entity.Entity_ID,
                            Entity_Name: entity.Entity_Name
                        },
                        Book: {
                            Entity_ID: this.app.dsSystemInfo.objectNode.Context.Book.Entity_ID
                        },
                        Book_Loc: {
                            Entity_ID: this.app.dsSystemInfo.objectNode.Context.Book_Loc.Entity_ID
                        },
                        Contract: {
                            Contract_ID: this.getControlValue('NewFiling.Contract.Contract_ID'),
                            Contract_Num: this.getControlValue('NewFiling.Contract.Contract_Num')
                        }
                    });
                }
            });
        }
    }

    clearNewFilings() {
        //delete new filings from state
        this.documentOS.deleteNewFilings();

        //clear entity, contract, claim search inputs
        this.form.controls.NewFiling.patchValue(
            {
                Claim: {
                    Claim_ID: null,
                    Book_Claim_Num: null
                },
                Contract: {
                    Contract_ID: null,
                    Contract_Num: null
                },
                Entity: {
                    Entity_ID: null,
                    Entity_Name: null
                }
            },
            { emitEvent: false }
        );
        this.filingReferanceChange.emit(true);
    }

    openAdvancedSearch() {
        this.searchSrv.openAdvancedSearchDialog(this.advancedSearchResult.bind(this));
    }

    advancedSearchResult(result) {
        if (result && result.Record_Type) {
            switch (result.Record_Type) {
                case 'Entity':
                    if (result.Entity_ID > 0) {
                        this.documentOS.addFiling({
                            Selected_Ind: 'Y',
                            Distribute: { Method: 'F' },

                            Entity: {
                                Role_Type: result.Role_Type,
                                Entity_ID: result.Entity_ID,
                                Entity_Name: result.Entity_Name
                            },
                            Book: {
                                Entity_ID: this.app.dsSystemInfo.objectNode.Context.Book.Entity_ID
                            },
                            Book_Loc: {
                                Entity_ID: this.app.dsSystemInfo.objectNode.Context.Book_Loc.Entity_ID
                            }
                        });
                    }
                    break;

                case 'Contract':
                    if (result.Contract_ID > 0) {
                        // update entity on form
                        this.form.controls.NewFiling.patchValue(
                            {
                                Entity: {
                                    Role_Type: 'CN',
                                    Entity_ID: result.Entity_ID,
                                    Entity_Name: result.Entity_Name
                                }
                            },
                            { emitEvent: false }
                        );

                        this.form.controls.NewFiling.patchValue(
                            {
                                Contract: {
                                    Contract_ID: result.Contract_ID,
                                    Contract_Num: result.Contract_Num
                                }
                            },
                            { emitEvent: false }
                        );

                        // clear claim on form
                        this.form.controls.NewFiling.patchValue(
                            {
                                Claim: {
                                    Claim_ID: null,
                                    Book_Claim_Num: null
                                }
                            },
                            { emitEvent: false }
                        );

                        // update contract/claim references on existing new filings
                        this.documentOS.updateFilingReferences({
                            Contract: {
                                Contract_ID: result.Contract_ID,
                                Contract_Num: result.Contract_Num
                            },
                            Claim: {
                                Claim_ID: null,
                                Book_Claim_Num: null
                            }
                        });

                        this.documentOS.addFiling({
                            Selected_Ind: 'Y',
                            Distribute: { Method: 'F' },
                            Entity: {
                                Role_Type: 'CN',
                                Entity_ID: result.Entity_ID,
                                Entity_Name: result.Entity_Name
                            },
                            Book: {
                                Entity_ID: this.app.dsSystemInfo.objectNode.Context.Book.Entity_ID
                            },
                            Book_Loc: {
                                Entity_ID: this.app.dsSystemInfo.objectNode.Context.Book_Loc.Entity_ID
                            },
                            Contract: {
                                Contract_ID: result.Contract_ID,
                                Contract_Num: result.Contract_Num
                            }
                        });
                    }
                    break;

                case 'Claim':
                    if (result.Claim_ID > 0) {
                        this.form.controls.NewFiling.patchValue(
                            {
                                Entity: {
                                    Role_Type: 'CN',
                                    Entity_ID: result.Entity_ID,
                                    Entity_Name: result.Entity_Name
                                },
                                Contract: {
                                    Contract_ID: result.Contract_ID,
                                    Contract_Num: result.Contract_Num
                                },
                                Claim: {
                                    Claim_ID: result.Claim_ID,
                                    Book_Claim_Num: result.Book_Claim_Num
                                }
                            },
                            { emitEvent: false }
                        );

                        // update contract/claim references on existing new filings
                        this.documentOS.updateFilingReferences({
                            Contract: {
                                Contract_ID: result.Contract_ID,
                                Contract_Num: result.Contract_Num
                            },
                            Claim: {
                                Claim_ID: result.Claim_ID,
                                Book_Claim_Num: result.Book_Claim_Num
                            }
                        });

                        this.documentOS.addFiling({
                            Selected_Ind: 'Y',
                            Distribute: { Method: 'F' },
                            Entity: {
                                Role_Type: 'CN',
                                Entity_ID: result.Entity_ID,
                                Entity_Name: result.Entity_Name
                            },
                            Book: {
                                Entity_ID: this.app.dsSystemInfo.objectNode.Context.Book.Entity_ID
                            },
                            Book_Loc: {
                                Entity_ID: this.app.dsSystemInfo.objectNode.Context.Book_Loc.Entity_ID
                            },
                            Contract: {
                                Contract_ID: result.Contract_ID,
                                Contract_Num: result.Contract_Num
                            },
                            Claim: {
                                Claim_ID: result.Claim_ID,
                                Book_Claim_Num: result.Book_Claim_Num
                            }
                        });
                    }
                    break;
            }
            this.filingReferanceChange.emit(true);
        }
    }

    getSelectedEntityRole(entity: any) {
        let selectedRole;
        const roles = entity?.Roles?.Role;

        if (roles && roles.length > 0) {
            for (const role of roles) {
                if ('Selected' in role && role.Selected) {
                    selectedRole = role.Role_Type;
                    break;
                }
            }

            if (!selectedRole) {
                selectedRole = roles[0].Role_Type;
            }
        }

        if (!selectedRole) {
            selectedRole = '';
        }

        return selectedRole;
    }

    docClassSelected(data: any) {
        let defaults = { File_Drawer: '?', File_Folder: '?' };
        if (data) {
            defaults.File_Drawer = data.Option_Code_2 || '?';
            defaults.File_Folder = data.Option_Code_3 || '?';
        }

        this.documentOS.setDefaultDrawerFolder(defaults, true);
    }
}
